import type { Grade } from '@/config/scoring';
import { getMessageSlotCount } from '@/config/messages';
import { captureError } from '@/sentry';

export interface LevelProgress {
  highScore: number;
  highGrade: Grade | '';
  highAccuracy: number;
  completedAt: string | null;
}

export interface GameProgress {
  levels: LevelProgress[];
  selectedDifficulty: string;
}

const PROGRESS_KEY = 'morse-hero-progress';
const TOTAL_LEVELS = getMessageSlotCount();

function createDefaultProgress(): GameProgress {
  const levels: LevelProgress[] = [];
  for (let i = 0; i < TOTAL_LEVELS; i++) {
    levels.push({
      highScore: 0,
      highGrade: '',
      highAccuracy: 0,
      completedAt: null,
    });
  }
  return { levels, selectedDifficulty: 'easy' };
}

export function loadProgress(): GameProgress {
  try {
    const saved = localStorage.getItem(PROGRESS_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      const defaults = createDefaultProgress();
      // Merge with defaults to handle new levels being added
      const levels = defaults.levels.map((def, i) => {
        const rec = parsed?.levels?.[i];
        if (!rec || typeof rec !== 'object') return def;
        const partial = rec as Partial<LevelProgress> & { completed?: boolean };
        const completedAt = typeof partial.completedAt === 'string'
          ? partial.completedAt
          : partial.completed
            ? new Date().toISOString()
            : null;
        const highAccuracy = typeof partial.highAccuracy === 'number' ? partial.highAccuracy : def.highAccuracy;
        const { completed: _completed, ...rest } = partial;
        return {
          ...def,
          ...rest,
          highAccuracy,
          completedAt,
        };
      });
      return {
        levels,
        selectedDifficulty: parsed.selectedDifficulty ?? defaults.selectedDifficulty,
      };
    }
  } catch (error) {
    captureError(error, { scope: 'progress', action: 'loadProgress' });
  }
  return createDefaultProgress();
}

export function saveProgress(progress: GameProgress): void {
  try {
    localStorage.setItem(PROGRESS_KEY, JSON.stringify(progress));
  } catch (error) {
    captureError(error, { scope: 'progress', action: 'saveProgress' });
  }
}

export function resetLocalProgress(): GameProgress {
  try {
    localStorage.removeItem(PROGRESS_KEY);
  } catch (error) {
    captureError(error, { scope: 'progress', action: 'resetProgress' });
  }
  return createDefaultProgress();
}

export function updateLevelProgress(
  levelIndex: number,
  score: number,
  completed: boolean,
  grade: Grade,
  accuracy: number
): { newHighScore: boolean; unlockedLevel: number | null; completionRecorded: boolean } {
  const progress = loadProgress();
  const level = progress.levels[levelIndex];

  if (!level) {
    return { newHighScore: false, unlockedLevel: null, completionRecorded: false };
  }

  const newHighScore = score > level.highScore;
  if (newHighScore) {
    level.highScore = score;
    level.highGrade = grade;
    level.highAccuracy = accuracy;
  }

  const hadCompleted = Boolean(level.completedAt);
  const completionRecorded = completed && !hadCompleted;
  if (completionRecorded) {
    level.completedAt = new Date().toISOString();
  }

  let unlockedLevel: number | null = null;

  // If completed for the first time, unlock next level
  if (completionRecorded) {
    const nextIndex = levelIndex + 1;
    if (nextIndex < progress.levels.length) {
      unlockedLevel = nextIndex;
    }
  }

  saveProgress(progress);

  return { newHighScore, unlockedLevel, completionRecorded };
}

export function isLevelUnlocked(levelIndex: number): boolean {
  const progress = loadProgress();
  if (levelIndex < 0 || levelIndex >= progress.levels.length) {
    console.warn(`isLevelUnlocked called with invalid levelIndex: ${levelIndex}`);
    return false;
  }
  if (levelIndex === 0) return true;
  return Boolean(progress.levels[levelIndex - 1]?.completedAt);
}

export function getLevelHighScore(levelIndex: number): number {
  const progress = loadProgress();
  if (levelIndex < 0 || levelIndex >= progress.levels.length) {
    console.warn(`getLevelHighScore called with invalid levelIndex: ${levelIndex}`);
    return 0;
  }
  return progress.levels[levelIndex].highScore;
}

export function getLevelHighGrade(levelIndex: number): Grade | '' {
  const progress = loadProgress();
  if (levelIndex < 0 || levelIndex >= progress.levels.length) {
    console.warn(`getLevelHighGrade called with invalid levelIndex: ${levelIndex}`);
    return '';
  }
  return progress.levels[levelIndex].highGrade ?? '';
}

export function isLevelCompleted(levelIndex: number): boolean {
  const progress = loadProgress();
  if (levelIndex < 0 || levelIndex >= progress.levels.length) {
    console.warn(`isLevelCompleted called with invalid levelIndex: ${levelIndex}`);
    return false;
  }
  return Boolean(progress.levels[levelIndex].completedAt);
}

export function getSelectedDifficulty(): string {
  const progress = loadProgress();
  return progress.selectedDifficulty;
}

export function setSelectedDifficulty(difficulty: string): void {
  const progress = loadProgress();
  progress.selectedDifficulty = difficulty;
  saveProgress(progress);
}
